import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import {
  FaCalendarAlt, FaFileExcel, FaFilePdf, FaFilter, FaChartBar,
  FaUtensils, FaWineGlass, FaSync, FaTimes, FaSort, FaSortUp, FaSortDown
} from "react-icons/fa";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData from "../../functions/fetchData";
import "./ItemWiseSaleReport.css";

export default function ItemWiseSaleReport() {
  const [foodData, setFoodData] = useState([]);
  const [barData, setBarData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('food');
  const [showFilters, setShowFilters] = useState(false);
  const [showMonthlySummary, setShowMonthlySummary] = useState(false);
  const [groupByItems, setGroupByItems] = useState(false);
  const [showEntertainmentOnly, setShowEntertainmentOnly] = useState(false);
  const [showNonEntertainmentOnly, setShowNonEntertainmentOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    itemName: "",
    minQuantity: "",
    maxQuantity: ""
  });

  const fetchFoodSales = async () => {
    try {
      setLoading(true);
      const response = await fetchData("food_cart", null, "id", {});
      setFoodData(response || []);
      if (activeTab === 'food') {
        setFilteredData(response || []);
      }
    } catch (error) {
      console.error("Error fetching food sales:", error);
      toast.error("Failed to fetch food sales data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBarSales = async () => {
    try {
      setLoading(true);
      const response = await fetchData("bar_cart", null, "id", {});
      setBarData(response || []);
      if (activeTab === 'bar') {
        setFilteredData(response || []);
      }
    } catch (error) {
      console.error("Error fetching bar sales:", error);
      toast.error("Failed to fetch bar sales data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFoodSales();
    fetchBarSales();
  }, []);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [activeTab, foodData, barData, filters, showEntertainmentOnly, showNonEntertainmentOnly]);

  const applyFilters = () => {
    const sourceData = activeTab === 'food' ? foodData : barData;
    
    let filtered = sourceData.filter(item => {
      const itemDate = item.setup_dte ? new Date(item.setup_dte) : null;
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      const dateMatch = 
        (!startDate || !itemDate || itemDate >= startDate) &&
        (!endDate || !itemDate || itemDate <= endDate);

      const itemNameMatch = 
        !filters.itemName || 
        (item.item_name && item.item_name.toLowerCase().includes(filters.itemName.toLowerCase()));

      const quantityMatch = 
        (!filters.minQuantity || parseFloat(item.quantity || 0) >= parseFloat(filters.minQuantity)) &&
        (!filters.maxQuantity || parseFloat(item.quantity || 0) <= parseFloat(filters.maxQuantity));

      const entertainmentMatch = 
        !showEntertainmentOnly || 
        (item.mode && item.mode.toLowerCase() === 'entertainment');

      const nonEntertainmentMatch = 
        !showNonEntertainmentOnly || 
        (!item.mode || item.mode.toLowerCase() !== 'entertainment');

      return dateMatch && itemNameMatch && quantityMatch && entertainmentMatch && nonEntertainmentMatch;
    });

    setFilteredData(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      itemName: "",
      minQuantity: "",
      maxQuantity: ""
    });
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    const sourceData = tab === 'food' ? foodData : barData;
    setFilteredData(sourceData);
    clearFilters();
    setShowMonthlySummary(false);
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
  };

  // Sorting helpers for the data tables
  const numericSortKeys = ['totalSales', 'itemCount', 'quantity', 'totalQuantity', 'avgRate', 'totalAmount', 'rate', 'amount'];
  const dateSortKeys = ['dte', 'month'];

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortRows = (rows, key, direction) => {
    if (!key) return rows;
    const dir = direction === 'desc' ? -1 : 1;

    return [...rows].sort((a, b) => {
      let valA = a[key];
      let valB = b[key];

      if (dateSortKeys.includes(key)) {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      } else if (numericSortKeys.includes(key)) {
        valA = parseFloat(valA) || 0;
        valB = parseFloat(valB) || 0;
      } else {
        valA = (valA || '').toString().toLowerCase();
        valB = (valB || '').toString().toLowerCase();
      }

      if (valA < valB) return -1 * dir;
      if (valA > valB) return 1 * dir;
      return 0;
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="iw-sort-icon" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="iw-sort-icon iw-sort-active" />
      : <FaSortDown className="iw-sort-icon iw-sort-active" />;
  };

  const generateMonthlySummary = () => {
    const summary = {};
    
    filteredData.forEach(item => {
      if (!item.dte) return;
      
      const date = new Date(item.dte);
      const monthKey = format(date, 'MMM yyyy');
      const total = parseFloat(item.amount || 0);
      
      if (!summary[monthKey]) {
        summary[monthKey] = {
          month: monthKey,
          totalSales: 0,
          itemCount: 0,
          quantity: 0
        };
      }
      
      summary[monthKey].totalSales += total;
      summary[monthKey].itemCount += 1;
      summary[monthKey].quantity += parseFloat(item.quantity || 0);
    });

    return Object.values(summary).sort((a, b) => {
      const dateA = new Date(a.month);
      const dateB = new Date(b.month);
      return dateB - dateA;
    });
  };

  const monthlySummaryData = generateMonthlySummary();

  const generateGroupedByItems = () => {
    const grouped = {};
    
    filteredData.forEach(item => {
      const itemName = item.item_name || 'Unknown Item';
      
      if (!grouped[itemName]) {
        grouped[itemName] = {
          item_name: itemName,
          category: item.category || '-',
          type: item.type || '-',
          totalQuantity: 0,
          totalAmount: 0,
          avgRate: 0,
          count: 0
        };
      }
      
      grouped[itemName].totalQuantity += parseFloat(item.quantity || 0);
      grouped[itemName].totalAmount += parseFloat(item.amount || 0);
      grouped[itemName].count += 1;
    });

    return Object.values(grouped).map(item => {
      item.avgRate = item.totalAmount / item.totalQuantity || 0;
      return item;
    }).sort((a, b) => b.totalAmount - a.totalAmount);
  };

  const groupedItemsData = generateGroupedByItems();

  const exportToPDF = () => {
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    
    // Header with border
    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(0.5);
    doc.rect(margin, 10, pageWidth - (margin * 2), 25);
    
    // Company/Report Title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(41, 128, 185);
    doc.text(`${activeTab === 'food' ? 'FOOD' : 'BAR'} SALE REPORT`, pageWidth / 2, 20, { align: 'center' });
    
    // Date Range
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100);
    let dateRange = 'Period: All Dates';
    if (filters.startDate || filters.endDate) {
      dateRange = `Period: ${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`;
    }
    doc.text(dateRange, pageWidth / 2, 27, { align: 'center' });
    
    // Generation Date
    doc.setFontSize(8);
    doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, pageWidth / 2, 32, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    
    let startY = 40;
    
    if (showMonthlySummary) {
      const summaryHeaders = [['Month', 'Total Sales (THB)', 'Items', 'Quantity']];
      const summaryRows = monthlySummaryData.map(item => [
        item.month,
        item.totalSales.toFixed(2),
        item.itemCount.toString(),
        item.quantity.toFixed(2)
      ]);
      
      const totalSales = monthlySummaryData.reduce((sum, item) => sum + item.totalSales, 0);
      const totalItems = monthlySummaryData.reduce((sum, item) => sum + item.itemCount, 0);
      const totalQty = monthlySummaryData.reduce((sum, item) => sum + item.quantity, 0);
      
      summaryRows.push(['GRAND TOTAL', totalSales.toFixed(2), totalItems.toString(), totalQty.toFixed(2)]);
      
      doc.autoTable({
        startY: startY,
        head: summaryHeaders,
        body: summaryRows,
        theme: 'striped',
        headStyles: { 
          fillColor: [41, 128, 185],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10,
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [50, 50, 50]
        },
        columnStyles: {
          0: { halign: 'left', cellWidth: 50 },
          1: { halign: 'right', cellWidth: 50 },
          2: { halign: 'right', cellWidth: 40 },
          3: { halign: 'right', cellWidth: 40 }
        },
        footStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: 'bold'
        },
        margin: { left: margin, right: margin },
        didParseCell: function (data) {
          if (data.row.index === monthlySummaryData.length && data.section === 'body') {
            data.cell.styles.fillColor = [255, 235, 59];
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 10;
          }
        },
        didDrawPage: function (data) {
          // Footer
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      });
    } else if (groupByItems) {
      const headers = [['Item Name', 'Category', 'Type', 'Total Qty', 'Avg Rate (THB)', 'Total Amount (THB)']];
      const rows = groupedItemsData.map(item => [
        item.item_name,
        item.category || '-',
        item.type || '-',
        item.totalQuantity.toFixed(2),
        item.avgRate.toFixed(2),
        item.totalAmount.toFixed(2)
      ]);
      
      const totalQty = groupedItemsData.reduce((sum, item) => sum + item.totalQuantity, 0);
      const totalAmount = groupedItemsData.reduce((sum, item) => sum + item.totalAmount, 0);
      
      rows.push(['GRAND TOTAL', '', '', totalQty.toFixed(2), '', totalAmount.toFixed(2)]);
      
      doc.autoTable({
        startY: startY,
        head: headers,
        body: rows,
        theme: 'striped',
        headStyles: { 
          fillColor: [40, 167, 69],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10,
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [50, 50, 50]
        },
        columnStyles: {
          0: { halign: 'left', cellWidth: 60 },
          1: { halign: 'center', cellWidth: 30 },
          2: { halign: 'center', cellWidth: 25 },
          3: { halign: 'right', cellWidth: 25 },
          4: { halign: 'right', cellWidth: 25 },
          5: { halign: 'right', cellWidth: 30 }
        },
        margin: { left: margin, right: margin },
        didParseCell: function (data) {
          if (data.row.index === groupedItemsData.length && data.section === 'body') {
            data.cell.styles.fillColor = [255, 235, 59];
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 10;
          }
        },
        didDrawPage: function (data) {
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      });
    } else {
      const headers = [['Date', 'Invoice', 'Item Name', 'Mode', 'Qty', 'Rate (THB)', 'Amount (THB)']];
      const rows = filteredData.map(item => [
        item.dte ? format(new Date(item.dte), 'dd/MM/yyyy') : '',
        item.invoice?.toString() || '',
        item.item_name || '',
        item.mode || '',
        parseFloat(item.quantity || 0).toFixed(2),
        parseFloat(item.rate || 0).toFixed(2),
        parseFloat(item.amount || 0).toFixed(2)
      ]);
      
      const totalQty = filteredData.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
      const totalAmount = filteredData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
      
      rows.push(['', '', '', 'GRAND TOTAL', totalQty.toFixed(2), '', totalAmount.toFixed(2)]);
      
      doc.autoTable({
        startY: startY,
        head: headers,
        body: rows,
        theme: 'striped',
        headStyles: { 
          fillColor: [220, 53, 69],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 23 },
          1: { halign: 'center', cellWidth: 20 },
          2: { halign: 'left', cellWidth: 50 },
          3: { halign: 'center', cellWidth: 25 },
          4: { halign: 'right', cellWidth: 18 },
          5: { halign: 'right', cellWidth: 22 },
          6: { halign: 'right', cellWidth: 25 }
        },
        margin: { left: margin, right: margin },
        didParseCell: function (data) {
          if (data.row.index === filteredData.length && data.section === 'body') {
            data.cell.styles.fillColor = [255, 235, 59];
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 9;
          }
        },
        didDrawPage: function (data) {
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      });
    }
    
    doc.save(`${activeTab}_sale_report_${format(new Date(), 'ddMMyyyy')}.pdf`);
    toast.success('PDF exported successfully');
  };

  const prepareCSVData = () => {
    if (showMonthlySummary) {
      return monthlySummaryData.map(item => ({
        'Month': item.month,
        'Total Sales': item.totalSales.toFixed(2),
        'Items Count': item.itemCount,
        'Total Quantity': item.quantity.toFixed(2)
      }));
    } else if (groupByItems) {
      return groupedItemsData.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category || '-',
        'Type': item.type || '-',
        'Total Quantity': item.totalQuantity.toFixed(2),
        'Avg Rate': item.avgRate.toFixed(2),
        'Total Amount': item.totalAmount.toFixed(2)
      }));
    } else {
      return filteredData.map(item => ({
        'Date': item.dte ? format(new Date(item.dte), 'dd/MM/yyyy') : '',
        'Invoice': item.invoice || '',
        'Item Name': item.item_name || '',
        'Mode': item.mode || '',
        'Quantity': item.quantity || 0,
        'Rate': parseFloat(item.rate || 0).toFixed(2),
        'Amount': parseFloat(item.amount || 0).toFixed(2)
      }));
    }
  };

  const csvData = prepareCSVData();

  // Pagination logic
  const displayData = sortRows(groupByItems ? groupedItemsData : filteredData, sortConfig.key, sortConfig.direction);
  const sortedMonthlySummaryData = sortRows(monthlySummaryData, sortConfig.key, sortConfig.direction);
  const totalPages = Math.ceil(displayData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  const calculateTotals = () => {
    const totalQuantity = filteredData.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
    const totalAmount = filteredData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    
    return { totalQuantity, totalAmount };
  };

  const totals = calculateTotals();

  return (
    <Layout>
      <Header title="📊 Item Wise Sale Report" />
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="item-wise-sale-container">
        {/* Tab Switcher */}
        <div className="iw-card">
          <div className="iw-card-body iw-p-2">
            <div className="iw-btn-group">
              <button
                className={`iw-btn ${activeTab === 'food' ? 'iw-btn-success' : 'iw-btn-outline-success'}`}
                onClick={() => switchTab('food')}
              >
                <FaUtensils className="iw-icon-spacing" /> Food Sales
              </button>
              <button
                className={`iw-btn ${activeTab === 'bar' ? 'iw-btn-warning' : 'iw-btn-outline-warning'}`}
                onClick={() => switchTab('bar')}
              >
                <FaWineGlass className="iw-icon-spacing" /> Bar Sales
              </button>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="iw-card">
          <div className="iw-card-header">
            <button
              className="iw-btn iw-btn-primary iw-btn-full"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className="iw-icon-spacing" /> {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
          
          {showFilters && (
            <div className="iw-card-body">
              <div className="iw-row">
                <div className="iw-col-md-3 iw-col-6">
                  <label className="iw-form-label">
                    <FaCalendarAlt className="iw-icon-spacing" /> Start Date
                  </label>
                  <input
                    type="date"
                    className="iw-form-control"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
                <div className="iw-col-md-3 iw-col-6">
                  <label className="iw-form-label">
                    <FaCalendarAlt className="iw-icon-spacing" /> End Date
                  </label>
                  <input
                    type="date"
                    className="iw-form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
                <div className="iw-col-md-4 iw-col-6">
                  <label className="iw-form-label">Item Name</label>
                  <input
                    type="text"
                    className="iw-form-control"
                    placeholder="Search item..."
                    value={filters.itemName}
                    onChange={(e) => handleFilterChange('itemName', e.target.value)}
                  />
                </div>
                <div className="iw-col-md-2 iw-col-6">
                  <label className="iw-form-label">Min Qty</label>
                  <input
                    type="number"
                    className="iw-form-control"
                    placeholder="0"
                    value={filters.minQuantity}
                    onChange={(e) => handleFilterChange('minQuantity', e.target.value)}
                  />
                </div>
                <div className="iw-col-md-2 iw-col-6">
                  <label className="iw-form-label">Max Qty</label>
                  <input
                    type="number"
                    className="iw-form-control"
                    placeholder="999"
                    value={filters.maxQuantity}
                    onChange={(e) => handleFilterChange('maxQuantity', e.target.value)}
                  />
                </div>
                <div className="iw-col-md-2 iw-col-12 iw-d-flex iw-align-items-end">
                  <button
                    className="iw-btn iw-btn-danger iw-btn-full"
                    onClick={clearFilters}
                  >
                    <FaTimes className="iw-icon-spacing" /> Clear
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="iw-card">
          <div className="iw-card-body">
            <div className="iw-row">
              <div className="iw-col-md-2 iw-col-6">
                <button
                  className="iw-btn iw-btn-info iw-btn-full"
                  onClick={() => {
                    setShowMonthlySummary(!showMonthlySummary);
                    if (!showMonthlySummary) setGroupByItems(false);
                    setSortConfig({ key: null, direction: 'asc' });
                  }}
                >
                  <FaChartBar className="iw-icon-spacing" /> 
                  {showMonthlySummary ? 'Detailed' : 'Monthly'}
                </button>
              </div>
              <div className="iw-col-md-2 iw-col-6">
                <button
                  className={`iw-btn ${showEntertainmentOnly ? 'iw-btn-warning' : 'iw-btn-outline-warning'} iw-btn-full`}
                  onClick={() => {
                    setShowEntertainmentOnly(!showEntertainmentOnly);
                    if (!showEntertainmentOnly) setShowNonEntertainmentOnly(false);
                    setCurrentPage(1);
                  }}
                  disabled={showMonthlySummary}
                  title="Filter Entertainment Mode Only"
                >
                  🎭 Entertainment
                </button>
              </div>
              <div className="iw-col-md-2 iw-col-6">
                <button
                  className={`iw-btn ${showNonEntertainmentOnly ? 'iw-btn-info' : 'iw-btn-outline-info'} iw-btn-full`}
                  onClick={() => {
                    setShowNonEntertainmentOnly(!showNonEntertainmentOnly);
                    if (!showNonEntertainmentOnly) setShowEntertainmentOnly(false);
                    setCurrentPage(1);
                  }}
                  disabled={showMonthlySummary}
                  title="Exclude Entertainment Mode"
                >
                  🚫 No Entertainment
                </button>
              </div>
              <div className="iw-col-md-2 iw-col-6">
                <button
                  className={`iw-btn ${groupByItems ? 'iw-btn-success' : 'iw-btn-outline-success'} iw-btn-full`}
                  onClick={() => {
                    setGroupByItems(!groupByItems);
                    if (!groupByItems) setShowMonthlySummary(false);
                    setCurrentPage(1);
                    setSortConfig({ key: null, direction: 'asc' });
                  }}
                  disabled={showMonthlySummary}
                >
                  <FaUtensils className="iw-icon-spacing" /> 
                  {groupByItems ? 'All' : 'Group'}
                </button>
              </div>
              <div className="iw-col-md-2 iw-col-6">
                <button className="iw-btn iw-btn-danger iw-btn-full" onClick={exportToPDF}>
                  <FaFilePdf className="iw-icon-spacing" /> PDF
                </button>
              </div>
              <div className="iw-col-md-2 iw-col-6">
                <CSVLink
                  data={csvData}
                  filename={`${activeTab}_sale_report_${format(new Date(), 'ddMMyyyy')}.csv`}
                  className="iw-btn iw-btn-success iw-btn-full"
                >
                  <FaFileExcel className="iw-icon-spacing" /> Excel
                </CSVLink>
              </div>
              <div className="iw-col-md-1 iw-col-6">
                <button
                  className="iw-btn iw-btn-secondary iw-btn-full"
                  onClick={() => {
                    fetchFoodSales();
                    fetchBarSales();
                  }}
                  title="Refresh Data"
                >
                  <FaSync />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="iw-row">
          <div className="iw-col-md-4 iw-col-6">
            <div className="iw-summary-card primary">
              <small>{groupByItems ? 'Unique Items' : 'Total Records'}</small>
              <h5>{displayData.length}</h5>
            </div>
          </div>
          <div className="iw-col-md-4 iw-col-6">
            <div className="iw-summary-card info">
              <small>Total Quantity</small>
              <h5>{totals.totalQuantity.toFixed(2)}</h5>
            </div>
          </div>
          <div className="iw-col-md-4 iw-col-12">
            <div className="iw-summary-card success">
              <small>Total Amount</small>
              <h5>฿{totals.totalAmount.toFixed(2)}</h5>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="iw-card">
          <div className="iw-card-body-zero">
            <div className="iw-table-container">
              {loading ? (
                <div className="iw-loading">
                  <div className="iw-spinner" role="status">
                    <span className="iw-visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : showMonthlySummary ? (
                <table className="iw-table">
                  <thead>
                    <tr>
                      <th className="iw-col-min-120 iw-sortable" onClick={() => handleSort('month')}>Month {renderSortIcon('month')}</th>
                      <th className="iw-text-end iw-col-min-150 iw-sortable" onClick={() => handleSort('totalSales')}>Total Sales {renderSortIcon('totalSales')}</th>
                      <th className="iw-text-end iw-col-min-100 iw-sortable" onClick={() => handleSort('itemCount')}>Items {renderSortIcon('itemCount')}</th>
                      <th className="iw-text-end iw-col-min-120 iw-sortable" onClick={() => handleSort('quantity')}>Quantity {renderSortIcon('quantity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMonthlySummaryData.length > 0 ? (
                      sortedMonthlySummaryData.map((item, index) => (
                        <tr key={index}>
                          <td>{item.month}</td>
                          <td className="iw-text-end">฿{item.totalSales.toFixed(2)}</td>
                          <td className="iw-text-end">{item.itemCount}</td>
                          <td className="iw-text-end">{item.quantity.toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="iw-text-center iw-text-muted iw-p-3">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : groupByItems ? (
                <table className="iw-table">
                  <thead>
                    <tr>
                      <th className="iw-col-min-150 iw-sortable" onClick={() => handleSort('item_name')}>Item Name {renderSortIcon('item_name')}</th>
                      <th className="iw-col-min-100 iw-sortable" onClick={() => handleSort('category')}>Category {renderSortIcon('category')}</th>
                      <th className="iw-col-min-100 iw-sortable" onClick={() => handleSort('type')}>Type {renderSortIcon('type')}</th>
                      <th className="iw-text-end iw-col-min-100 iw-sortable" onClick={() => handleSort('totalQuantity')}>Total Qty {renderSortIcon('totalQuantity')}</th>
                      <th className="iw-text-end iw-col-min-100 iw-sortable" onClick={() => handleSort('avgRate')}>Avg Rate {renderSortIcon('avgRate')}</th>
                      <th className="iw-text-end iw-col-min-120 iw-sortable" onClick={() => handleSort('totalAmount')}>Total Amount {renderSortIcon('totalAmount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((item, index) => (
                        <tr key={index + indexOfFirstItem}>
                          <td>{item.item_name}</td>
                          <td>{item.category || '-'}</td>
                          <td>{item.type || '-'}</td>
                          <td className="iw-text-end">{item.totalQuantity.toFixed(2)}</td>
                          <td className="iw-text-end">฿{item.avgRate.toFixed(2)}</td>
                          <td className="iw-text-end">฿{item.totalAmount.toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="iw-text-center iw-text-muted iw-p-3">
                          No grouped items found
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {currentItems.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="iw-text-end">TOTAL:</td>
                        <td className="iw-text-end">{totals.totalQuantity.toFixed(2)}</td>
                        <td></td>
                        <td className="iw-text-end">฿{totals.totalAmount.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              ) : (
                <table className="iw-table">
                  <thead>
                    <tr>
                      <th className="iw-col-min-90 iw-sortable" onClick={() => handleSort('dte')}>Date {renderSortIcon('dte')}</th>
                      <th className="iw-col-min-100 iw-sortable" onClick={() => handleSort('invoice')}>Invoice {renderSortIcon('invoice')}</th>
                      <th className="iw-col-min-150 iw-sortable" onClick={() => handleSort('item_name')}>Item Name {renderSortIcon('item_name')}</th>
                      <th className="iw-col-min-100 iw-sortable" onClick={() => handleSort('mode')}>Mode {renderSortIcon('mode')}</th>
                      <th className="iw-text-end iw-col-min-70 iw-sortable" onClick={() => handleSort('quantity')}>Qty {renderSortIcon('quantity')}</th>
                      <th className="iw-text-end iw-col-min-80 iw-sortable" onClick={() => handleSort('rate')}>Rate {renderSortIcon('rate')}</th>
                      <th className="iw-text-end iw-col-min-100 iw-sortable" onClick={() => handleSort('amount')}>Amount {renderSortIcon('amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((item, index) => (
                        <tr key={index + indexOfFirstItem}>
                          <td>{item.dte ? format(new Date(item.dte), 'dd/MM/yyyy') : ''}</td>
                          <td>{item.invoice || '-'}</td>
                          <td>{item.item_name || '-'}</td>
                          <td>
                            {item.mode ? (
                              <span className={`iw-mode-badge ${item.mode.toLowerCase() === 'entertainment' ? 'entertainment' : ''}`}>
                                {item.mode.toLowerCase() === 'entertainment' && '🎭 '}
                                {item.mode}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="iw-text-end">{parseFloat(item.quantity || 0).toFixed(2)}</td>
                          <td className="iw-text-end">฿{parseFloat(item.rate || 0).toFixed(2)}</td>
                          <td className="iw-text-end">฿{parseFloat(item.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="iw-text-center iw-text-muted iw-p-3">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {filteredData.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="iw-text-end">TOTAL:</td>
                        <td className="iw-text-end">{totals.totalQuantity.toFixed(2)}</td>
                        <td></td>
                        <td className="iw-text-end">฿{totals.totalAmount.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Pagination Controls */}
        {!showMonthlySummary && displayData.length > 0 && (
          <div className="iw-card">
            <div className="iw-card-body iw-p-2">
              <div className="iw-pagination-container">
                <div className="iw-pagination-info">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, displayData.length)} of {displayData.length} records
                </div>
                
                <div className="iw-pagination-controls">
                  <button
                    className="iw-pagination-btn"
                    onClick={prevPage}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  
                  <select
                    className="iw-pagination-select"
                    value={currentPage}
                    onChange={(e) => setCurrentPage(Number(e.target.value))}
                  >
                    {[...Array(totalPages)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    className="iw-pagination-btn"
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
                
                <div className="iw-pagination-size">
                  <select
                    className="iw-pagination-select"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10/page</option>
                    <option value={20}>20/page</option>
                    <option value={50}>50/page</option>
                    <option value={100}>100/page</option>
                    <option value={displayData.length}>All</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
